/**
 * SPLIT-LEAN/B — strips legacy per-exercise programmazione fields
 * (`sets`, `weeks`, `precompiledRef`) from each `Split.exercises[i]` on read.
 *
 * Replaces `exerciseLegacySetsNormalizer` (which used to promote legacy `sets`
 * onto `weeks[0]`). Per architect scope (comment c896dbc8 §5.4): now that
 * per-exercise programmazione is gone, the legacy fields are simply dropped
 * client-side before they enter React state. On next save, the BE persists
 * the lean shape and the row is permanently migrated (lazy strategy).
 *
 * The function is intentionally typed at the boundary as `<T>(prog: T): T`
 * (returns the same nominal shape as the input). The legacy `sets`/`weeks`/
 * `precompiledRef` keys are not part of the lean runtime contract any more,
 * but the type alias `ExerciseEntry` still lists them as a transitional
 * compatibility surface for SPLIT-LEAN/C-doomed code. After C, the type
 * fields and this normalizer's runtime stripping both shrink to nothing.
 *
 * Pure function — does not mutate the input. Tolerant of partial / undefined
 * shapes (existing prod rows carry varying combinations of the legacy fields).
 */

export function stripLegacyExerciseFields<T>(programmazione: T): T {
  const p = programmazione as unknown as {
    splits?: Array<{
      exercises?: Array<Record<string, unknown>>;
      [key: string]: unknown;
    }> | null;
    [key: string]: unknown;
  };
  const splits = p.splits ?? [];
  return {
    ...(p as object),
    splits: splits.map((split) => ({
      ...split,
      exercises: (split?.exercises ?? []).map((exercise) => {
        // Destructure-drop the legacy fields.
        const { sets, weeks, precompiledRef, ...rest } = exercise;
        void sets;
        void weeks;
        void precompiledRef;
        return rest;
      }),
    })),
  } as unknown as T;
}
